import KeyboardXingshanv1 from "@sparklinkplayjoy/sdk-keyboard";
import KeyboardXingshan from "@sparklinkplayjoy/sdk-keyboard-v2";
// import { Keyboard } from "@bytech/web-hid-sdk/keyboard";
import { useGlobalStore } from "../../stores/globalStore";
import { syncBeiyingSdkKeyLibrary } from "./beiyingSdkKeyLibrary";
import { HFDDevice } from "@hfd-sdk/keyboard-sdk";
import { isDeviceSessionStandbyMode } from "../../utils/deviceSessionPresence/standbyGuard";
import {
  initActiveDriverTab,
  wrapHardwareEventEmitter,
  getTabScopedEquipmentData,
} from "@eweadn-qiankun/shared-utils";
import { patchXingshanUpgradeReconnection } from "./xingshanUpgradeReconnectFix";
import type { XDAdapter } from "./rongyuan/sdk/adapter";

type CurrentEquipmentType = {
  displayname: string;
  keyboardType: string;
  productId: number;
  productName: string;
  solutionRovider: string;
  vendorId: number;
  /** 与 shared-utils SDK_KEYBOARD_MOUSE_TYPE 对齐，如机甲键盘 beiyingMachine */
  sdkType?: number;
  /** 机甲键盘在设备表里统一记为 916（M71/M80 均走 916 协议） */
  machineBoard?: "916";
  /** 华奋达磁轴（battle68 等） */
  magneticAxis?: boolean;
  identifier?: string;
  /** 星闪双灯位 / 点阵屏等配置表能力（律动同步时须保留） */
  hasDoubleLighting?: boolean;
  hasDotMatrixScreen?: boolean;
  doubleLightingPositionLocked?: "upper" | "lower";
  brand?: string;
} | null;

// 全局变量存储当前的设备数据
let currentEquipmentData: CurrentEquipmentType = {
  displayname: "",
  keyboardType: "",
  productId: 0,
  productName: "",
  solutionRovider: "",
  vendorId: 0,
};

// 启动阶段预取 916 键表（含基础/动作/效率/工具/多媒体/系统编辑/窗口/修饰/常规键）
// 供贝盈虚拟键盘映射兜底使用（例如 ES98 静态布局与机甲键值空间对齐）。
syncBeiyingSdkKeyLibrary();

let lastEquipmentKey = "";
let lastLocalEquipmentData = "";
let lastParsedLocalEquipmentData: CurrentEquipmentType = null;

// 订阅 store 变化
useGlobalStore.subscribe((state: any) => {
  const tabScoped = getTabScopedEquipmentData();
  const localEquipmentData =
    tabScoped && Object.keys(tabScoped).length > 0
      ? JSON.stringify(tabScoped)
      : localStorage.getItem("equipmentData") || "";
  if (localEquipmentData !== lastLocalEquipmentData) {
    lastLocalEquipmentData = localEquipmentData;
    if (!localEquipmentData) {
      lastParsedLocalEquipmentData = null;
    } else {
      try {
        lastParsedLocalEquipmentData = JSON.parse(localEquipmentData);
      } catch {
        lastParsedLocalEquipmentData = null;
      }
    }
  }

  const newEquipmentData: any =
    state.equipmentData || lastParsedLocalEquipmentData;
  const key = newEquipmentData
    ? `${newEquipmentData.vendorId ?? ""}:${newEquipmentData.productId ?? ""}:${newEquipmentData.displayname ?? ""}:${newEquipmentData.solutionRovider ?? ""}`
    : "";

  if (key === lastEquipmentKey) {
    return;
  }
  lastEquipmentKey = key;

  // 确保solutionRovider不为空
  if (newEquipmentData && newEquipmentData.solutionRovider) {
    currentEquipmentData = newEquipmentData;
  } else if (!newEquipmentData) {
    // 如果新的数据为空，则重置为初始状态
    currentEquipmentData = {
      displayname: "",
      keyboardType: "",
      productId: 0,
      productName: "",
      solutionRovider: "",
      vendorId: 0,
    };
  }
});

/** 子应用 webpack 会编译本文件，勿用 `@/` 别名；与 deviceSession 键名保持一致 */
function readEquipmentDataFromStorage(): CurrentEquipmentType {
  if (typeof window === "undefined") return null;
  const tabScoped = getTabScopedEquipmentData();
  if (tabScoped?.solutionRovider) {
    return tabScoped as CurrentEquipmentType;
  }
  try {
    const raw =
      window.sessionStorage.getItem("eweadn:session:equipmentData") ??
      window.localStorage.getItem("equipmentData");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CurrentEquipmentType;
    return parsed?.solutionRovider ? parsed : null;
  } catch {
    return null;
  }
}

/** 与 deviceSession 键名一致；勿依赖 deviceSession 模块（子应用无法解析其 @/ 别名） */
function readSolutionRoviderNameFromStorage(): string {
  if (typeof window === "undefined") return "";
  return (
    window.sessionStorage.getItem("eweadn:session:solutionRoviderName") ??
    window.localStorage.getItem("solutionRoviderName") ??
    ""
  );
}

/** 解析当前会话应路由的键盘方案商（equipment → session/local solutionRoviderName） */
export function resolveActiveSolutionProvider(): string {
  const eq = getCurrentEquipmentData();
  if (eq?.solutionRovider) {
    return String(eq.solutionRovider);
  }
  return readSolutionRoviderNameFromStorage();
}

/** 是否存在可用于路由 SDK 的设备上下文 */
export function hasActiveKeyboardEquipmentContext(): boolean {
  if (resolveActiveSolutionProvider()) {
    return true;
  }
  const eq = getCurrentEquipmentData();
  if (eq?.vendorId || eq?.productId) {
    return true;
  }
  const stored = readEquipmentDataFromStorage();
  return Boolean(stored?.vendorId || stored?.solutionRovider);
}

// 返回当前设备数据的函数，确保solutionRovider不为空
export const getCurrentEquipmentData = (): CurrentEquipmentType => {
  const tabScoped = getTabScopedEquipmentData();
  if (tabScoped?.solutionRovider) {
    return tabScoped as CurrentEquipmentType;
  }
  if (currentEquipmentData?.solutionRovider) {
    return currentEquipmentData;
  }
  if (lastParsedLocalEquipmentData?.solutionRovider) {
    return lastParsedLocalEquipmentData;
  }
  try {
    const raw = localStorage.getItem("equipmentData");
    if (raw) {
      const parsed = JSON.parse(raw) as CurrentEquipmentType;
      if (parsed?.solutionRovider) return parsed;
    }
  } catch {
    /* ignore */
  }
  const fromStorage = readEquipmentDataFromStorage();
  if (fromStorage) {
    currentEquipmentData = fromStorage;
    return fromStorage;
  }
  if (lastParsedLocalEquipmentData?.solutionRovider) {
    currentEquipmentData = lastParsedLocalEquipmentData;
    return lastParsedLocalEquipmentData;
  }
  return null;
};

function bootstrapCurrentEquipmentFromStorage(): void {
  const eq = readEquipmentDataFromStorage();
  if (eq) {
    currentEquipmentData = eq;
    lastEquipmentKey = `${eq.vendorId ?? ""}:${eq.productId ?? ""}:${eq.displayname ?? ""}:${eq.solutionRovider ?? ""}`;
  }
}
bootstrapCurrentEquipmentFromStorage();
initActiveDriverTab();

export const serviceKeyboardv1 = new KeyboardXingshanv1({
  configs: [],
  usage: 0x01,
  usagePage: [0xffb0],
});

export const xingshanRawServiceKeyboard = new KeyboardXingshan({
  configs: [],
  usage: 0x01,
  usagePage: [0xffb0],
});

patchXingshanUpgradeReconnection(xingshanRawServiceKeyboard);

const rawServiceKeyboard = xingshanRawServiceKeyboard;

wrapHardwareEventEmitter(serviceKeyboardv1);
wrapHardwareEventEmitter(xingshanRawServiceKeyboard);

/** 建链/初始化本身不可再触发 ensure，否则会递归 */
const XINGSHAN_SDK_RAW_METHODS = new Set([
  "getDevices",
  "init",
  "closeDevice",
  "constructor",
  /** 升级流程内含 closeDevice / Boot 重连，升级前勿 ensure 再 init 干扰 */
  "upgrade",
  "appToBoot",
  "bootToApp",
  "resetUpgradeStatus",
]);

let ensureXingshanInFlight: Promise<boolean> | null = null;
let lastEnsuredXingshanKey = "";

type XingshanEquipmentLike = {
  displayname?: string;
  keyboardType?: string;
  productId?: number;
  productName?: string;
  solutionRovider?: string;
  vendorId?: number;
  sdkType?: number;
  machineBoard?: string;
  /** 配置表能力字段，律动同步时须保留，否则灯光页双灯/点阵判定会丢 */
  hasDoubleLighting?: boolean;
  hasDotMatrixScreen?: boolean;
  doubleLightingPositionLocked?: "upper" | "lower";
  identifier?: string;
  magneticAxis?: boolean;
  brand?: string;
};

function sameXingshanDeviceIdentity(
  a: { vendorId?: number; productId?: number } | null | undefined,
  b: { vendorId?: number; productId?: number } | null | undefined
): boolean {
  return (
    Number(a?.vendorId ?? NaN) === Number(b?.vendorId ?? NaN) &&
    Number(a?.productId ?? NaN) === Number(b?.productId ?? NaN) &&
    Number.isFinite(Number(a?.vendorId)) &&
    Number.isFinite(Number(a?.productId))
  );
}

/**
 * 全局律动下发前同步内存中的 equipmentData，避免 Proxy 按旧设备 closeDevice+init 抢绑。
 * 律动 bind 走 xingshanSession，与 getCurrentEquipmentData 不同步时 setLightingCustom 会写到错误 HID。
 * 须保留 hasDoubleLighting 等能力字段，否则桌面端灯光页虚拟键盘会丢双灯分半样式。
 */
export function syncXingshanSdkEquipmentContext(
  eq: XingshanEquipmentLike | null | undefined
): void {
  if (!eq || String(eq.solutionRovider ?? "") !== "xingshan") return;
  const prev =
    currentEquipmentData?.solutionRovider === "xingshan" &&
    sameXingshanDeviceIdentity(currentEquipmentData, eq)
      ? (currentEquipmentData as Record<string, unknown>)
      : null;
  const incoming = eq as Record<string, unknown>;
  const normalized = {
    ...(prev ?? {}),
    ...incoming,
    displayname: String(eq.displayname ?? eq.productName ?? ""),
    keyboardType: String(eq.keyboardType ?? ""),
    productId: Number(eq.productId ?? 0),
    productName: String(eq.productName ?? ""),
    solutionRovider: "xingshan",
    vendorId: Number(eq.vendorId ?? 0),
    sdkType: eq.sdkType ?? prev?.sdkType,
    machineBoard: (eq.machineBoard ?? prev?.machineBoard) as "916" | undefined,
  } as NonNullable<CurrentEquipmentType> & Record<string, unknown>;
  currentEquipmentData = normalized;
  lastEquipmentKey = `${normalized.vendorId ?? ""}:${normalized.productId ?? ""}:${normalized.displayname ?? ""}:${normalized.solutionRovider ?? ""}`;
  lastEnsuredXingshanKey = `${normalized.vendorId ?? ""}:${normalized.productId ?? ""}:${normalized.productName ?? ""}:${normalized.keyboardType ?? ""}`;
}

/** 内页 F5 后子应用可能在 onRetry 完成前调用 SDK，须先 bind 到 equipmentData 对应设备 */
export async function ensureXingshanSdkReady(): Promise<void> {
  const eq = getCurrentEquipmentData();
  if (eq?.solutionRovider !== "xingshan") return;
  const key = `${eq.vendorId ?? ""}:${eq.productId ?? ""}:${eq.productName ?? ""}:${eq.keyboardType ?? ""}`;
  if (lastEnsuredXingshanKey === key && !ensureXingshanInFlight) return;
  if (!ensureXingshanInFlight) {
    ensureXingshanInFlight = import("../../utils/connectDevice/sdk/xingshan")
      .then((m) =>
        m.initXingshanSdkForConfiguration(eq, {
          /** presence 由连接链路启动；此处再 start 会在子应用副本上叠第二路心跳 */
          skipPresenceRestart: true,
        })
      )
      .then((ok) => {
        if (ok) lastEnsuredXingshanKey = key;
        return ok;
      })
      .finally(() => {
        ensureXingshanInFlight = null;
      });
  }
  await ensureXingshanInFlight;
}

export const serviceKeyboard = new Proxy(rawServiceKeyboard, {
  get(target, prop, receiver) {
    const value = Reflect.get(target, prop, receiver);
    if (typeof value !== "function") return value;
    const name = String(prop);
    if (name === "closeDevice") {
      return (...args: unknown[]) => {
        lastEnsuredXingshanKey = "";
        return value.apply(target, args);
      };
    }
    if (XINGSHAN_SDK_RAW_METHODS.has(name)) {
      return value.bind(target);
    }
    return (...args: unknown[]) => {
      if (isDeviceSessionStandbyMode()) {
        /** Standby 不碰 HID：读操作返回 null，写操作 reject */
        if (name.startsWith("get")) {
          return Promise.resolve(null);
        }
        return Promise.reject(
          new Error("[device-session] Standby HID write blocked")
        );
      }
      return ensureXingshanSdkReady().then(() => value.apply(target, args));
    };
  },
});

/**
 * SDK 内置 NEED_FULL_DEVICE_IDS 未覆盖、但本仓 2.4G 需完整 ID
 *（vendorId:productId:productName → …:manufacturer:product）的接收器。
 * 与内置列表合并；勿写已内置的 `3141:65278:2.4G Dongle`。
 */
const HFD_NEED_FULL_DEVICE_IDS = [
  "3141:65273:2.4G Dongle", // X87 V2（0xfef9）
  "3141:65274:2.4G Dongle", // X75-Pro / X98 等（0xfefa）
] as const;

export const hfdServiceKeyboard = {
  instance: new HFDDevice({
    needFullDeviceIds: [...HFD_NEED_FULL_DEVICE_IDS],
  }),
};
wrapHardwareEventEmitter(hfdServiceKeyboard.instance);
// setDevicesHuafenda();

/** @bytech/web-hid-sdk `Keyboard` */
export const beiyingKeyboard = {
  // instance: null! as Keyboard, // 初始化空值，指定类型
  instance: null as any, // 初始化空值，指定类型
};
/** X98 1K：@bytech/web-sdk-machine `Device8815` */
export const beiyingKeyboardX98_1K_8815 = {
  instance: null as any, // 初始化空值，指定类型
};
/** 机甲：Device916 / DeviceWireLess916 */
export const beiyingMachineKeyboard = {
  instance: null as any,
  /** 当前 instance 对应的贝盈键盘 logicalKey；社区分享重绑依赖此字段 */
  attachedLogicalKey: null as string | null,
};
// const { keyboard } = await initBeiyingDevice(2);
// beiyingKeyboard.instance = keyboard;

/** 2.4G连接 刷新后 SDK 挂载较慢，适当拉长等待 */
const BEIYING_SDK_READY_TIMEOUT_MS = 60_000;
const BEIYING_SDK_READY_INTERVAL_MS = 200;

function isBeiyingSDKReady(inst: any): boolean {
  if (!inst) return false;
  return !!(
    typeof inst.getDeviceInfo === "function" &&
    inst.lighting &&
    typeof inst.lighting.getEffect === "function" &&
    inst.remapping &&
    (typeof inst.remapping.getKeymaps === "function" ||
      typeof inst.remapping.getKeymap === "function") &&
    inst.macro &&
    typeof inst.macro.getMacros === "function" &&
    inst.advancedKey &&
    typeof inst.advancedKey.getAdvancedKey === "function" &&
    inst.profile
  );
}

/** Device8815 就绪（勿与 web-hid `Keyboard` 混判） */
function isBeiying8815MachineReady(inst: any): boolean {
  if (!inst) return false;
  return !!(
    typeof inst.getKeyboardPower === "function" &&
    typeof inst.getKeyboardKeys === "function" &&
    typeof inst.setKeyboardKeys === "function"
  );
}

/** 仅 @bytech/web-hid-sdk 的 `beiyingKeyboard` */
export async function waitForBeiyingHidKeyboardReady(): Promise<boolean> {
  const deadline = Date.now() + BEIYING_SDK_READY_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (isBeiyingSDKReady(beiyingKeyboard.instance)) {
      return true;
    }
    await new Promise((r) => setTimeout(r, BEIYING_SDK_READY_INTERVAL_MS));
  }
  return false;
}

/** 仅 `beiyingKeyboardX98_1K_8815`（Device8815） */
export async function waitForBeiying8815KeyboardReady(): Promise<boolean> {
  const deadline = Date.now() + BEIYING_SDK_READY_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (isBeiying8815MachineReady(beiyingKeyboardX98_1K_8815.instance)) {
      return true;
    }
    await new Promise((r) => setTimeout(r, BEIYING_SDK_READY_INTERVAL_MS));
  }
  return false;
}

function isBeiyingMachineSDKReady(inst: any): boolean {
  return !!(
    inst &&
    typeof inst.getDeviceBasicAttributes === "function" &&
    typeof inst.getKeyboardKeys === "function"
  );
}

/** 等待「贝盈机甲」@bytech/web-sdk-machine 实例就绪（主应用连接后挂载在 beiyingMachineKeyboard） */
export async function waitForBeiyingMachineSDKReady(): Promise<boolean> {
  if (isBeiyingMachineSDKReady(beiyingMachineKeyboard.instance)) {
    return true;
  }
  const deadline = Date.now() + BEIYING_SDK_READY_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (isBeiyingMachineSDKReady(beiyingMachineKeyboard.instance)) {
      return true;
    }
    await new Promise((r) => setTimeout(r, BEIYING_SDK_READY_INTERVAL_MS));
  }
  return false;
}

/** 2.4G 单条 SDK 命令超时（避免 BoardList / 改键页长时间挂起） */
export const BEIYING_SDK_COMMAND_TIMEOUT_MS = 8_000;

export async function withBeiyingCommandTimeout<T>(
  promise: Promise<T>,
  ms: number = BEIYING_SDK_COMMAND_TIMEOUT_MS,
  label = "beiying"
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} 命令超时`)), ms)
    ),
  ]);
}

export const apiSuccessCode = (message: string) => {
  return {
    code: 200,
    message,
  };
};
export const apiErrorCode = (message: string) => {
  return {
    code: 204,
    message,
  };
};

/** 贝盈机甲 API 在 SDK 未挂载时的统一失败结构 */
export const beiyingMachineSdkNotReadyResponse = () => ({
  ...apiErrorCode("贝盈机甲键盘 SDK 尚未就绪，请确认设备已连接"),
  data: null,
});

/** 贝盈 API 在 SDK 未挂载时的统一失败结构 */
export const beiyingSdkNotReadyResponse = () => ({
  ...apiErrorCode("贝盈 SDK 尚未就绪，请确认设备已连接"),
  data: null,
});

/** 容园（荣源）机械键盘 XD 协议适配器 */
let xdAdapterInstance: XDAdapter | null = null;

export async function initializeXDAdapter(deviceConfig: {
  vendorId: number;
  productId: number;
  productName?: string;
  displayname?: string;
  keyboardType?: string;
}): Promise<XDAdapter> {
  const { XDAdapter: XDAdapterClass } = await import("./rongyuan/sdk/adapter");
  xdAdapterInstance = await XDAdapterClass.create(deviceConfig);
  return xdAdapterInstance;
}

export function getXDAdapter(): XDAdapter | null {
  return xdAdapterInstance;
}

export async function destroyXDAdapter(): Promise<void> {
  if (!xdAdapterInstance) return;
  try {
    await xdAdapterInstance.destroy();
  } finally {
    xdAdapterInstance = null;
  }
}

export function isXDAdapterInitialized(): boolean {
  return xdAdapterInstance !== null;
}
